import { marks, measures } from 'domains/reporter/RichTextEditor/analytics/performance';
import type { AnalyticEventName } from 'modules/analytics/analytics';
import { createMarkForId, performance } from 'utils/performance';

type LifecycleMeasure = Readonly<{
  name: AnalyticEventName;
  startMark: string;
  endMark: string;
}>;

const scheduleConfirmedPaint = (
  callback: () => void,
  onVisibilityLost: () => void
): (() => void) => {
  let activeAnimationFrameId: number | null = null;
  let listeningForVisibilityChanges = false;
  const removeVisibilityChangeListener = () => {
    if (!listeningForVisibilityChanges) return;
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    listeningForVisibilityChanges = false;
  };
  const invalidateIfHidden = (): boolean => {
    if (document.visibilityState === 'visible') return false;
    removeVisibilityChangeListener();
    onVisibilityLost();
    return true;
  };
  const handleVisibilityChange = () => {
    invalidateIfHidden();
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  listeningForVisibilityChanges = true;

  activeAnimationFrameId = requestAnimationFrame(() => {
    activeAnimationFrameId = requestAnimationFrame(() => {
      activeAnimationFrameId = null;
      if (invalidateIfHidden()) return;
      removeVisibilityChangeListener();
      callback();
    });
  });

  return () => {
    removeVisibilityChangeListener();
    if (activeAnimationFrameId != null) {
      cancelAnimationFrame(activeAnimationFrameId);
      activeAnimationFrameId = null;
    }
  };
};

/**
 * Shared lifecycle bookkeeping for one recording start or stop attempt: a per-attempt requested
 * mark, a single confirmed-paint measurement, and idempotent disposal that clears the mark.
 */
const createLifecycleMeasurement = ({
  attemptId,
  requestedMark,
  detail,
  onDispose,
}: {
  attemptId: number;
  requestedMark: string;
  detail: Record<string, boolean>;
  onDispose: () => void;
}) => {
  const id = String(attemptId);
  const keyedRequestedMark = createMarkForId(requestedMark, id);
  let disposed = false;
  let paintScheduled = false;
  let cancelPaint = () => {};

  const dispose = (): void => {
    if (disposed) return;
    disposed = true;
    cancelPaint();
    performance.clearMarks(keyedRequestedMark);
    onDispose();
  };

  const measure = (lifecycleMeasure: LifecycleMeasure): void => {
    if (disposed) return;
    const keyedEndMark = createMarkForId(lifecycleMeasure.endMark, id);
    performance.mark(keyedEndMark);
    performance.measure(lifecycleMeasure.name, {
      start: createMarkForId(lifecycleMeasure.startMark, id),
      end: keyedEndMark,
      detail,
    });
    performance.clearMarks(keyedEndMark);
  };

  performance.mark(keyedRequestedMark);

  return {
    measure,
    /**
     * Measures the phase after a visible paint opportunity, then disposes. Samples taken while
     * the document is hidden are discarded rather than reported, since animation frames are
     * throttled there and the duration would reflect tab visibility instead of app latency.
     */
    scheduleUiPaintMeasure: (lifecycleMeasure: LifecycleMeasure): void => {
      if (disposed || paintScheduled) return;
      paintScheduled = true;
      if (document.visibilityState !== 'visible') {
        dispose();
        return;
      }
      cancelPaint = scheduleConfirmedPaint(() => {
        measure(lifecycleMeasure);
        dispose();
      }, dispose);
    },
    cancel: dispose,
  };
};

export type RecordingStartMeasurement = Readonly<{
  measureRecorderStarted: () => void;
  scheduleUiPaint: () => void;
  cancel: () => void;
}>;

export const createRecordingStartMeasurement = ({
  attemptId,
  shouldUseASRPlex,
  hadPendingStop,
  onDispose,
}: {
  attemptId: number;
  shouldUseASRPlex: boolean;
  hadPendingStop: boolean;
  onDispose: () => void;
}): RecordingStartMeasurement => {
  const measurement = createLifecycleMeasurement({
    attemptId,
    requestedMark: marks.sirona.Dictation.RecordingStart.Requested,
    // Stable metadata only: does not depend on internal step ordering, so it survives refactors.
    detail: { shouldUseASRPlex, hadPendingStop },
    onDispose,
  });

  return {
    measureRecorderStarted: () =>
      measurement.measure(measures.sirona.Dictation.RecordingStartRecorderStarted),
    scheduleUiPaint: () =>
      measurement.scheduleUiPaintMeasure(measures.sirona.Dictation.RecordingStartUiPainted),
    cancel: measurement.cancel,
  };
};

export type RecordingStopMeasurement = Readonly<{
  measureRecorderStopped: () => void;
  scheduleUiPaint: () => void;
  cancel: () => void;
}>;

export const createRecordingStopMeasurement = ({
  attemptId,
  shouldUseASRPlex,
  hadPendingStart,
  onDispose,
}: {
  attemptId: number;
  shouldUseASRPlex: boolean;
  hadPendingStart: boolean;
  onDispose: () => void;
}): RecordingStopMeasurement => {
  const measurement = createLifecycleMeasurement({
    attemptId,
    requestedMark: marks.sirona.Dictation.RecordingStop.Requested,
    // Stable metadata only: does not depend on internal step ordering, so it survives refactors.
    detail: { shouldUseASRPlex, hadPendingStart },
    onDispose,
  });

  return {
    measureRecorderStopped: () =>
      measurement.measure(measures.sirona.Dictation.RecordingStopRecorderStopped),
    scheduleUiPaint: () =>
      measurement.scheduleUiPaintMeasure(measures.sirona.Dictation.RecordingStopUiPainted),
    cancel: measurement.cancel,
  };
};
